/**
 * Personal-key transport for /api/proxy and /api/gemini-proxy.
 *
 * Wire format:   x-da-user-key: <providerId>:<base64(keyValue)>
 *
 * The base64 keeps the value stable through any forward proxy or CDN
 * normalisation that might otherwise strip non-token characters.
 *
 * Encoding lives client-side; decoding lives server-side. This module
 * exposes both so server.ts and the data-provider fetch wrapper share
 * one source of truth for the format and the hostname → provider map.
 */
import { API_PROVIDERS, type ApiProviderId } from '../config/apiProviders';
import type { UserApiKeys } from '../types/siteRag';

/**
 * Maps the upstream hostname the server proxies to the registered
 * provider id whose userKeyField holds the relevant personal key.
 *
 * NREL Solar and NREL Wind share the same env var (NREL_API_KEY) and
 * the same userKeyField ('nrelKey'), so picking either id resolves to
 * the same key. We deterministically use 'nrel-solar'.
 *
 * Hostnames not listed here have no managed user key, and the encoder
 * returns null for them — their requests fall through to the org key.
 */
export const HOSTNAME_TO_PROVIDER_ID: Record<string, ApiProviderId> = {
  'developer.nrel.gov': 'nrel-solar',
  'api.eia.gov': 'eia',
  'www.airnowapi.org': 'airnow',
};

/** Reverse of HOSTNAME_TO_PROVIDER_ID; useful for the server-side decoder. */
export function hostnameForProvider(providerId: ApiProviderId): string | null {
  for (const [host, id] of Object.entries(HOSTNAME_TO_PROVIDER_ID)) {
    if (id === providerId) return host;
  }
  return null;
}

function encodeBase64(value: string): string {
  if (typeof btoa === 'function') return btoa(unescape(encodeURIComponent(value)));
  // Node fallback — used only in server tests / replay scripts. Browsers always have btoa.
  return Buffer.from(value, 'utf8').toString('base64');
}

function decodeBase64(value: string): string | null {
  try {
    if (typeof Buffer !== 'undefined') return Buffer.from(value, 'base64').toString('utf8');
    if (typeof atob === 'function') return decodeURIComponent(escape(atob(value)));
  } catch {
    return null;
  }
  return null;
}

/**
 * Build the header value for a request to `targetUrl`, reading the matching
 * personal key from `userApiKeys`. Returns null when there is no managed
 * provider for the host or the user has not set a personal key.
 */
export function buildUserKeyHeader(targetUrl: string, userApiKeys: UserApiKeys | null | undefined): string | null {
  if (!userApiKeys) return null;
  let host: string;
  try {
    host = new URL(targetUrl).hostname;
  } catch {
    return null;
  }
  const providerId = HOSTNAME_TO_PROVIDER_ID[host];
  if (!providerId) return null;
  const provider = API_PROVIDERS[providerId];
  const value = userApiKeys[provider.userKeyField];
  if (typeof value !== 'string' || value.length === 0) return null;
  return `${providerId}:${encodeBase64(value)}`;
}

/** Build the header for the Gemini proxy. Same shape, providerId is always 'gemini'. */
export function buildGeminiUserKeyHeader(userApiKeys: UserApiKeys | null | undefined): string | null {
  if (!userApiKeys) return null;
  const value = userApiKeys.geminiKey;
  if (typeof value !== 'string' || value.length === 0) return null;
  return `gemini:${encodeBase64(value)}`;
}

export interface DecodedUserKey {
  providerId: ApiProviderId;
  key: string;
}

/**
 * Server-side decode. Returns null when the header is missing, malformed,
 * or carries a providerId we don't manage. Length-bounds the decoded key
 * so a giant header can't blow up downstream.
 */
export function decodeUserKeyHeader(headerValue: string | undefined | null): DecodedUserKey | null {
  if (!headerValue || typeof headerValue !== 'string') return null;
  const colonAt = headerValue.indexOf(':');
  if (colonAt <= 0) return null;
  const providerId = headerValue.slice(0, colonAt);
  const encoded = headerValue.slice(colonAt + 1);
  if (!Object.prototype.hasOwnProperty.call(API_PROVIDERS, providerId)) return null;
  if (encoded.length === 0 || encoded.length > 1024) return null;
  const key = decodeBase64(encoded);
  if (!key || key.length < 8 || key.length > 512) return null;
  return { providerId: providerId as ApiProviderId, key };
}

/* ── Lazy getter registry ──────────────────────────────────────────── */
/* The data-provider fetch wrapper is module-loaded before Zustand is   */
/* hydrated. We expose a one-shot setter that the store calls once at   */
/* boot, and a reader that yields {} until that registration happens.   */
/* This avoids a circular import (store → providers → store).           */

let _userApiKeysGetter: () => UserApiKeys = () => ({});

export function setUserApiKeysGetter(fn: () => UserApiKeys): void {
  _userApiKeysGetter = fn;
}

export function getCurrentUserApiKeys(): UserApiKeys {
  try {
    return _userApiKeysGetter() ?? {};
  } catch {
    return {};
  }
}
